/**
 * The words of "Last changes" (CU-869fn0gcp): the column labels of the heat map (Today / Tomorrow / the date, with
 * the weekday under), the window pills, the context line under the band and the unit line under the map. Times are
 * said in the account's time zone (D5): the server's stamps are naive UTC, the zone travels in `window.tz`.
 * Pure and alias-free: runs under `node --test`.
 */
import { fromIso } from '../../@services/dateRange.js'
import { addDaysIso } from './viewState.js'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export const WINDOW_OPTIONS = Object.freeze([
	{ value: 'last_pass', label: 'Last pass' },
	{ value: 'today', label: 'Today' },
	{ value: 'last_7d', label: 'Last 7 days' },
	{ value: 'custom', label: 'Custom…' },
])

/** The simulated filter of the band (v1.5): simulated decisions count like written ones, or are hidden. */
export const SIMULATED_OPTIONS = Object.freeze([
	{ value: 'include', label: 'Include' },
	{ value: 'exclude', label: 'Exclude' },
])

export const UNIT_OPTIONS = Object.freeze([
	{ value: 'total', label: 'Total' },
	{ value: 'day', label: 'Per day' },
])

/** `29 Oct` */
export function fmtDay(iso) {
	const d = fromIso(iso)
	return d ? `${d.getDate()} ${MONTHS[d.getMonth()]}` : ''
}

/** `Thu 29 Oct` */
export function fmtWeekday(iso) {
	const d = fromIso(iso)
	return d ? `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}` : ''
}

/** `{ top, sub }` of a date column: Today / Tomorrow / the date, and the weekday with the date under. */
export function columnLabel(iso, todayIso) {
	const top = iso === todayIso ? 'Today' : iso === addDaysIso(todayIso, 1) ? 'Tomorrow' : fmtDay(iso)
	return { top, sub: fmtWeekday(iso) }
}

/** The pick-up as the dialogs and the chips name it: `Today 29 Oct`, `Tomorrow 30 Oct`, `Mon 2 Nov`. */
export function pickupLabel(iso, todayIso) {
	if (iso === todayIso) return `Today ${fmtDay(iso)}`
	if (iso === addDaysIso(todayIso, 1)) return `Tomorrow ${fmtDay(iso)}`
	return fmtWeekday(iso)
}

const parts = (iso, tz) => {
	if (!iso) return null
	const d = new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`)
	if (Number.isNaN(d.getTime())) return null
	try {
		const f = new Intl.DateTimeFormat('en-GB', { timeZone: tz || 'UTC', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
		const p = Object.fromEntries(f.formatToParts(d).filter(x => x.type !== 'literal').map(x => [x.type, x.value]))
		return { day: p.day, month: p.month.replace('.', ''), hour: p.hour === '24' ? '00' : p.hour, minute: p.minute }
	} catch {
		return null
	}
}

/** `22 Oct 10:32` of a server stamp (naive UTC) in the account's zone; `''` without one. */
export function fmtInstant(iso, tz) {
	const p = parts(iso, tz)
	return p ? `${p.day} ${p.month} ${p.hour}:${p.minute}` : ''
}

/** `10:32` */
export function fmtTime(iso, tz) {
	const p = parts(iso, tz)
	return p ? `${p.hour}:${p.minute}` : ''
}

/** A pass id for a cell: `202610281032-…` (the slot digits of `rp-202610281032-schedule-a1b2c3`). */
export function shortPass(id) {
	const m = /^rp-(\d{12})/.exec(String(id || ''))
	return m ? `${m[1]}-…` : String(id || '')
}

const INVARIANT = new Set(['ACRISS'])
const plural = (n, word) => `${n} ${word}${n === 1 || INVARIANT.has(word) ? '' : word === 'pass' ? 'es' : 's'}`

/** The window as the band says it: the two instants, or the pass; with the zone. */
export function windowText(window) {
	if (!window) return ''
	const tz = window.tz || 'UTC'
	if (window.kind === 'last_pass') {
		if (!window.pass) return 'no pass yet'
		return `just before → after the pass of ${fmtInstant(window.pass.started_at, tz)} (${tz})`
	}
	return `${fmtInstant(window.from, tz)} → ${fmtInstant(window.to, tz)} (${tz})`
}

const excluded = data => data?.scope?.simulated === 'exclude'

/**
 * Criterion 10b: passes decided the sample in the window but none of their decisions counts (all already set, no
 * price change or not sent; simulated ones only under Exclude). Its own state — "No rate pushes between … and …" —
 * never a 0 % result. v1.5: empty = writes + (include ? simulated_writes : 0) == 0, whatever the account's mode; not
 * while the pass is still running; false when the server did not say.
 */
export function noPushes(data) {
	if (!data || !data.passes || data.writes == null) return false
	const counted = data.writes + (excluded(data) ? 0 : (data.simulated_writes || 0))
	if (counted !== 0) return false
	return !data.window?.pass?.in_progress
}

/** How many simulated decisions the Exclude filter is hiding when nothing else is left (0 otherwise). */
export function hiddenSimulated(data) {
	if (!excluded(data) || data.writes !== 0) return 0
	return data.simulated_writes || 0
}

/** The title of that state, with the window's two instants (or the pass) in the account's zone. */
export function noPushesTitle(data) {
	const w = data?.window || {}
	const tz = w.tz || 'UTC'
	if (w.kind === 'last_pass' && w.pass) return `No rate pushes in the pass of ${fmtInstant(w.pass.started_at, tz)} (${tz})`
	return `No rate pushes between ${fmtInstant(w.from, tz)} and ${fmtInstant(w.to, tz)} (${tz})`
}

/** The tier half of the context line. */
export function tierText(tier) {
	if (!tier) return ''
	if (tier.none) return 'no tier to compare with'
	const strategies = tier.strategies || []
	const from = strategies.length > 1 ? `${strategies.length} tiers, one per strategy` : strategies.length === 1 ? `from the strategy ${strategies[0].name}` : ''
	return `compared against ${plural(tier.providers || 0, 'provider')}${from ? `, ${from}` : ''}`
}

/**
 * The line under the band: office · length · pick-up dates · ACRISS · window · passes · tier (or "pass in progress").
 */
export function contextLine(data) {
	if (!data) return ''
	const s = data.sample || {}
	const officeText = s.office_name && s.office_name !== s.office ? `${s.office} · ${s.office_name}` : s.office || ''
	const bits = [officeText, plural(s.lor ?? 0, 'day'), plural((s.dates || []).length, 'pick-up date'),
		plural((s.acriss || []).length, 'ACRISS'), `window ${windowText(data.window)}`]
	if (data.window?.pass?.in_progress) bits.push('pass in progress, partial figures')
	else bits.push(plural(data.passes || 0, 'pass'))
	const tier = tierText(data.tier)
	if (tier) bits.push(tier)
	return bits.filter(Boolean).join(' · ')
}

/** Under the map: the unit of every figure. */
export function unitLine(unit, lor) {
	return unit === 'day' ? 'Price per day, EUR · tier mean vs your target shelf price' : `Total rental price, EUR · ${plural(lor, 'day')} · tier mean vs your target shelf price`
}

export function unitWord(unit) {
	return unit === 'day' ? 'per day' : 'total rental'
}

/** The headline's reading in words (criterion 9): the sign said, never a bare number. */
export function deltaSentence(deltaPt, ownChange, marketChange) {
	if (deltaPt == null) return ownChange == null ? 'no comparable cell' : 'no tier to compare with'
	if (Math.abs(deltaPt) < 0.05) return 'the engine moved like the market'
	const down = ownChange != null && ownChange < 0
	const marketDown = marketChange != null && marketChange < 0
	if (deltaPt < 0) {
		if (down && !marketDown && marketChange != null && marketChange > 0) return 'you went down while the market went up'
		return down ? 'the engine went down more than the market' : 'the engine went up less than the market'
	}
	if (!down && marketDown) return 'you went up while the market went down'
	return down ? 'the engine went down less than the market' : 'the engine went up more than the market'
}

export const daysLabel = n => plural(n, 'day')
